import { HttpClient } from '@angular/common/http';
import { Injectable, OnDestroy } from '@angular/core';
import {
  catchError,
  firstValueFrom,
  Observable,
  retry,
  shareReplay,
  startWith,
  Subject,
  switchMap,
  throwError,
} from 'rxjs';
import { environment } from '@environments/environment';
import { Department } from 'app/features/admin/models/department'; // Move to Shared / Core ?
import { CurrentUserDataDTO } from '@shared/models/currentUserDataDTO';
import { DepartmentDTO } from '@shared/models/departmentDTO';
import { UserDTO } from 'app/core/api/models/userDTO';

@Injectable({
  providedIn: 'root',
})
export class UserApiServiceCollection implements OnDestroy {
  private baseUrl = environment.baseUrl;

  private departmentController = 'management/departments';

  private userController = 'management/users';

  private adminController = 'management/admins';

  private readonly departmentRefresh = new Subject<void>();
  private readonly departmentList$: Observable<Department[]> = this.departmentRefresh.pipe(
    startWith(undefined),
    switchMap(() => this.http.get<Department[]>(this.baseUrl + this.departmentController + '/user').pipe(retry(3))),
    shareReplay(1),
  );

  constructor(private http: HttpClient) {}

  ngOnDestroy(): void {
    this.departmentRefresh.complete();
  }

  getDepartment(): Observable<Department[]> {
    return this.departmentList$;
  }

  getDepartmentById(departmentId: string): Observable<DepartmentDTO> {
    return this.http
      .get<DepartmentDTO>(this.baseUrl + this.departmentController + '/' + departmentId)
      .pipe(retry(1), catchError(this.formatErrors))
      .pipe(shareReplay(1));
  }

  async getUserByDepartmentId(departmentId: string): Promise<UserDTO[]> {
    const response = this.http
      .get<UserDTO[]>(this.baseUrl + this.userController + '/departments/' + departmentId)
      .pipe(retry(1), catchError(this.formatErrors))
      .pipe(shareReplay(1));
    return firstValueFrom(response);
  }

  async getAdminsByOrganizationId(organizationId: string): Promise<UserDTO[]> {
    const response = this.http
      .get<UserDTO[]>(this.baseUrl + this.adminController + '/organizations/' + organizationId)
      .pipe(retry(1), catchError(this.formatErrors))
      .pipe(shareReplay(1));
    return firstValueFrom(response);
  }

  getCurrentUserData(): Observable<CurrentUserDataDTO> {
    return this.http.get<CurrentUserDataDTO>(this.baseUrl + this.userController + '/current');
  }

  private formatErrors(error: any): Observable<never> {
    return throwError(() => new Error(error.error));
  }

  updateUser(user: UserDTO): Observable<UserDTO> {
    return this.http.put<UserDTO>(this.baseUrl + this.userController, user);
  }
}
